'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { OrganizationAvatar } from '@/components/Avatar';
import { IOrganization } from '@/types';
import useOrganization from '@/hooks/useOrganization';
import useOxenSession from '@/hooks/useOxenSession';

import * as Icon from '@/ui/Icon';
import { Stack, Row, Card } from '@/ui/primatives';

import * as Styled from './styled';
import Button from '@/ui/Button';
import { CLAIM_ORGANIZATION_URL, IS_RUNNING_ON_PREMISES } from '@/config/environment';

if (!IS_RUNNING_ON_PREMISES && !CLAIM_ORGANIZATION_URL) {
  throw new Error('NEXT_PUBLIC_CLAIM_ORGANIZATION_URL is not set');
}

const RepositoriesLink: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  const pathname = usePathname();
  const isActive = pathname === `/${organization?.name}`;
  return (
    <Link href={`/${organization?.name}`} className={isActive ? 'active' : ''}>
      <Row gap={10} alignItems="center">
        <Icon.Book />
        <span>Repositories</span>
      </Row>
    </Link>
  );
};

const UsersLink: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  const pathname = usePathname();
  const isActive = pathname === `/organizations/${organization?.name}/users`;
  return (
    <Link href={`/organizations/${organization?.name}/users`} className={isActive ? 'active' : ''}>
      <Row gap={10} alignItems="center">
        <Icon.Users />
        <span>Users</span>
      </Row>
    </Link>
  );
};

const SettingsLink: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  const pathname = usePathname();
  const isActive = pathname === `/organizations/${organization?.name}/settings`;
  return (
    <Link href={`/organizations/${organization?.name}/settings`} className={isActive ? 'active' : ''}>
      <Row gap={10} alignItems="center">
        <Icon.Cog />
        <span>Settings</span>
      </Row>
    </Link>
  );
};

const PlanLink: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  const pathname = usePathname();
  const isActive = pathname.includes(`/organizations/${organization?.name}/settings/billing`);
  return (
    <Link href={`/organizations/${organization?.name}/settings/billing`} className={isActive ? 'active' : ''}>
      <Row gap={10} alignItems="center">
        <Icon.CreditCard />
        <span>Billing and plans</span>
      </Row>
    </Link>
  );
};

const IntegrationsLink: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  const pathname = usePathname();
  const isActive = new RegExp(
    String.raw`\/organizations\/${organization?.name}\/(\bintegrations\b|integrations\/s)`,
  ).test(pathname);
  return (
    <Link href={`/organizations/${organization?.name}/integrations`} className={isActive ? 'active' : ''}>
      <Row gap={10} alignItems="center">
        <Icon.Plug />
        <span>Integrations</span>
      </Row>
    </Link>
  );
};

const OrganizationInfo: React.FC<{ organization: IOrganization }> = ({
  organization: initialOrganization,
  ...props
}) => {
  const { organization } = useOrganization({ orgName: initialOrganization.name, initialData: initialOrganization });
  const { authorization } = useOxenSession();

  const isAdmin = authorization.forOrganization(organization).canManage();
  const isMember = authorization.forOrganization(organization).canView();
  const claimOrganizationUrl = `${CLAIM_ORGANIZATION_URL}${initialOrganization.name}`;

  return organization ? (
    <Styled.OrganizationInfo {...props}>
      <Stack>
        <Stack gap={20}>
          <Row gap={10} alignItems="center">
            <OrganizationAvatar organization={organization} size={60} />
            <Stack gap={5}>
              <Link href={`/${organization?.name}`}>
                <h5>{organization?.display_name}</h5>
              </Link>
              <div>Organization Account</div>
            </Stack>
          </Row>
          {organization?.description && <p>{organization?.description}</p>}
        </Stack>
        <Styled.Links>
          <RepositoriesLink organization={organization} />
          {isAdmin && <SettingsLink organization={organization} />}
          {isAdmin && <IntegrationsLink organization={organization} />}
          {isAdmin && <PlanLink organization={organization} />}
          {isMember && <UsersLink organization={organization} />}
        </Styled.Links>
        {!organization.is_claimed && (
          <Card gap={5}>
            <h6>This organization is currently unclaimed.</h6>
            <p>
              If you represent this organization, we went ahead and indexed your public data. Just let us know,
              we&apos;re waiting to hand over control.
            </p>
            <Button.Basic href={claimOrganizationUrl} small target="_blank" rel="noopener noreferrer">
              Claim This Organization
            </Button.Basic>
          </Card>
        )}
      </Stack>
    </Styled.OrganizationInfo>
  ) : (
    <div>Loading...</div>
  );
};

const OrganizationSidebar: React.FC<{ organization: IOrganization }> = ({ organization, ...props }) => {
  return (
    <Styled.OrganizationSidebar {...props}>
      {organization && <OrganizationInfo organization={organization} />}
    </Styled.OrganizationSidebar>
  );
};

export default OrganizationSidebar;
